/* Form validation */
import { convertDateStrToDate } from './date_utils';
import { COOK_CYCLE_DAYS } from '@variables';
/* Determines if value is an integer. */
var isInt = function (value) { return value % 1 === 0; };
/* Returns a boolean indicating whether a weight input is valid. */
var isValidWeight = function (value, min, max) {
    return !(!value || !isInt(value) || value < min || value > max);
};
var EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
var EMOJI_REGEX = /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/;
var ZIPCODE_REGEX = /^[0-9]{5}$/;
/* From StackOverflow: https://stackoverflow.com/a/52619927 */
var PO_BOX_REGEX = /^\s*(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(b|box|boxes|bin)[-.\s]*)|.*((p|post)[-.\s]*(o|off|office)[-.\s]*)|.*((p|post)[-.\s]*(b|box|boxes|bin)[-.\s]*)|(box|boxes|bin)[-.\s]*)(#|n|num|number)?\s*\d+/i;
// Based on Sendgrid's invalid email list.
var COMMON_EMAIL_DOMAIN_NAMES = [
    'aol.com',
    'gmail.com',
    'hotmail.com',
    'yahoo.com',
    'outlook.com',
    'live.com',
];
/* Implemented based on https://en.wikipedia.org/wiki/Levenshtein_distance
  for measuring the difference between two sequences.
*/
var getLevenshteinDistance = function (str1, str2) {
    var string1Length = str1.length;
    var string2Length = str2.length;
    var matrix = [];
    // Create a matrix to store the Levenshtein distances
    // The matrix will be of size (string1Length + 1) x (string2Length + 1)
    // The extra row and column are used to store the distances for empty substrings
    for (var i = 0; i <= string1Length; i++) {
        matrix[i] = [i];
    }
    for (var j = 0; j <= string2Length; j++) {
        matrix[0][j] = j;
    }
    // Compute the Levenshtein distance for each substring
    for (var i = 1; i <= string1Length; i++) {
        for (var j = 1; j <= string2Length; j++) {
            // If the characters match, the distance is the same as for the previous substrings
            if (str1[i - 1] === str2[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            }
            else {
                // If the characters do not match, the distance is the minimum of the distances
                // for the previous substrings + 1.
                matrix[i][j] = Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1]) + 1;
            }
        }
    }
    return matrix[string1Length][string2Length];
};
var getMatchRate = function (str1, str2) {
    var distance = getLevenshteinDistance(str1, str2);
    var maxDistance = Math.max(str1.length, str2.length);
    return 1 - distance / maxDistance;
};
/* Validates an email address using basic logics and EMAIL_REGEX */
var isValidEmail = function (email) {
    if (!email) {
        return false;
    }
    var emailParts = email.split('@');
    // Email address should have username and domain name.
    if (emailParts.length !== 2) {
        return false;
    }
    var username = emailParts[0], domain = emailParts[1];
    if (username.length > 64 || domain.length > 255) {
        return false;
    }
    // We probably only want to support email with domain name and extension.
    // ie, xxxx.yyy is valid, but xxxx alone is not valid.
    if (!domain.includes('.')) {
        return false;
    }
    return EMAIL_REGEX.test(email);
};
var validateEmailAddress = function (email) {
    if (!isValidEmail(email)) {
        return {
            isValid: false,
            suggestion: null,
        };
    }
    var _a = email.split('@').map(function (part) { return part.toLowerCase(); }), username = _a[0], domain = _a[1];
    if (COMMON_EMAIL_DOMAIN_NAMES.includes(domain)) {
        return {
            isValid: true,
            suggestion: null,
        };
    }
    var domainSuggestion = null;
    var minimumMatchRate = 0.75;
    COMMON_EMAIL_DOMAIN_NAMES.forEach(function (validDomain) {
        var matchRate = getMatchRate(domain, validDomain);
        if (matchRate >= minimumMatchRate && matchRate < 1) {
            minimumMatchRate = matchRate;
            domainSuggestion = validDomain;
        }
    });
    return {
        isValid: true,
        suggestion: domainSuggestion ? "".concat(username, "@").concat(domainSuggestion) : null,
    };
};
/* Validates meal input, valid meal should be multiplier of 7. */
var isValidMealInput = function (value) {
    return value !== undefined && value % 7 === 0;
};
/* Gets the CSS invalid class selector based on value and isValid status. */
var getInvalidClass = function (value, isValid) {
    if (!value && value !== 0) {
        return '';
    }
    return value && !isValid ? 'invalid' : 'valid';
};
/* Strip out non digit from phone input and
  returns boolean indicating if phone input is valid. */
var isValidPhone = function (phone) {
    var result = phone.replace(/\D/g, '');
    return result.length === 10;
};
var eligibleStates = {
    AL: 'Alabama',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};
var ineligibleStates = {
    AK: 'Alaska',
    AS: 'American Samoa',
    FM: 'Federated States of Micronesia',
    GU: 'Guam',
    HI: 'Hawaii',
    MH: 'Marshall Islands',
    MP: 'Northern Mariana Islands',
    PR: 'Puerto Rico',
    VI: 'Virgin Islands',
};
var isValidCookDate = function (cookDateStr) {
    var cookDate = convertDateStrToDate(cookDateStr);
    if (!cookDate || !COOK_CYCLE_DAYS.includes(cookDate.getDay())) {
        return false;
    }
    var localNow = new Date();
    var dayAfterCookDate = new Date(cookDate);
    dayAfterCookDate.setDate(cookDate.getDate() + 1);
    // Local time check to loosely prevent selecting cook date in the past.
    if (localNow >= dayAfterCookDate) {
        return false;
    }
    return true;
};
var isValidName = function (value) {
    return !EMOJI_REGEX.test(value);
};
/* Ensures we get a first and last name */
var isValidFullName = function (value, isAdmin) {
    if (isAdmin === void 0) { isAdmin = false; }
    if (isAdmin) {
        return value.length > 1;
    }
    return value.trim().split(' ').length >= 2 && isValidName(value);
};
/* Validates that order has some pet meal packs. */
var isValidOrderPetMeals = function (orderPetMeals) {
    if (!orderPetMeals || !orderPetMeals.length) {
        return false;
    }
    return orderPetMeals.some(function (orderPetMeal) { return orderPetMeal.meals > 0; });
};
/* Validates that order has some order items. */
var isValidOrderItems = function (orderItems) {
    return orderItems.some(function (orderItem) { return orderItem.units > 0; });
};
/* Returns boolean indicating whether a value is valid. */
var isValidValue = function (name, value, isAdmin) {
    if (isAdmin === void 0) { isAdmin = false; }
    if (!value) {
        return false;
    }
    if (typeof value === 'string') {
        switch (name) {
            case 'street':
            case 'city':
                return value.length > 2;
            case 'state':
                return value.toUpperCase() in eligibleStates;
            case 'zipcode':
                return ZIPCODE_REGEX.test(value);
            case 'phone':
                return isValidPhone(value);
            case 'customerName':
            case 'vetName':
                return isValidFullName(value, isAdmin);
            case 'petName':
                return isValidName(value);
            case 'email':
                return isValidEmail(value);
            case 'cookDate':
                return isValidCookDate(value);
        }
    }
    if (Array.isArray(value)) {
        if (name === 'orderPetMeals' && isValidOrderPetMeals(value)) {
            return true;
        }
        if (name === 'items' && isValidOrderItems(value)) {
            return true;
        }
    }
    return false;
};
var validateAllAddressValues = function (street, city, state, zipcode, phone) {
    return {
        street: isValidValue('street', street),
        city: isValidValue('city', city),
        state: isValidValue('state', state),
        zipcode: isValidValue('zipcode', zipcode),
        phone: isValidValue('phone', phone),
    };
};
// Checks if 'PO Box' text exists in the following address inputs: street, apt, city, zipcode
// Excludes state (restricted to 2 characters) and phone (restricted to only numbers)
var addressContainsPOBox = function (street, apt, city, zipcode) {
    return (PO_BOX_REGEX.test(street) ||
        PO_BOX_REGEX.test(apt) ||
        PO_BOX_REGEX.test(city) ||
        PO_BOX_REGEX.test(zipcode));
};
// Checks if 'PO Box' text exists in the address input
var addressFieldContainsPOBox = function (value) {
    return PO_BOX_REGEX.test(value);
};
export { getInvalidClass, ineligibleStates, isInt, isValidCookDate, isValidName, isValidFullName, isValidValue, isValidWeight, isValidMealInput, PO_BOX_REGEX, eligibleStates, validateEmailAddress, validateAllAddressValues, addressContainsPOBox, addressFieldContainsPOBox, };
